import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { ArrowDownToLine, RefreshCw, AlertCircle } from 'lucide-react';
import PartnerLayout from '../../components/PartnerLayout';
import { partnerPortalAPI } from '../../lib/partnerApi';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const PartnerWithdrawalRequestPage = () => {
  const { partner } = usePartnerAuth();
  const [stats, setStats] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await partnerPortalAPI.dashboard();
      setStats(res.data.stats);
    } catch {} finally { setLoadingStats(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const partnerDoc = await partnerPortalAPI.getSettings();
      const bankDetails = partnerDoc.data?.partner?.bank_details || {};
      await partnerPortalAPI.requestWithdrawal({
        amount: Number(amount),
        payment_method: paymentMethod,
        payment_details: { notes, ...bankDetails },
      });
      toast.success('Withdrawal request submitted successfully!');
      setAmount('');
      setNotes('');
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit withdrawal request');
    } finally { setLoading(false); }
  };

  const pendingBalance = stats?.pending_balance || 0;

  return (
    <PartnerLayout>
      <div data-testid="partner-withdrawal-request" className="space-y-6 pb-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Withdrawal Request</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Request a payout of your pending balance</p>
        </div>

        {/* Balance card */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <p className="text-sm font-medium text-emerald-800">Available Balance</p>
          {loadingStats ? (
            <div className="flex items-center gap-2 mt-2"><RefreshCw className="w-4 h-4 animate-spin text-emerald-600" /><span className="text-muted-foreground text-sm">Loading…</span></div>
          ) : (
            <>
              <p className="text-4xl font-bold text-emerald-700 mt-1">${pendingBalance.toFixed(2)}</p>
              <p className="text-xs text-emerald-600 mt-1">${(stats?.total_withdrawn || 0).toFixed(2)} already withdrawn · ${(stats?.total_earnings || 0).toFixed(2)} total earned</p>
            </>
          )}
        </div>

        {pendingBalance <= 0 && !loadingStats && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">No balance available</p>
              <p className="text-xs text-amber-700 mt-0.5">You don't have any pending balance to withdraw yet.</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-card rounded-xl border border-border shadow-sm p-6">
          <h2 className="text-base font-semibold mb-4">Request Details</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Withdrawal Amount ($)</Label>
              <Input
                type="number"
                min="1"
                step="0.01"
                max={pendingBalance}
                placeholder={`Max: $${pendingBalance.toFixed(2)}`}
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="mt-1.5"
                data-testid="withdrawal-amount-input"
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="mt-1.5" data-testid="withdrawal-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Any special instructions…"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || pendingBalance <= 0}
              data-testid="withdrawal-submit-btn"
            >
              <ArrowDownToLine className="w-4 h-4 mr-2" />
              {loading ? 'Submitting…' : 'Submit Request'}
            </Button>
          </form>
        </div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerWithdrawalRequestPage;
